import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { FxAlert, FxButton, FxSpinner } from '@/components/fx'
import { templatesApi } from '@/lib/api'

import { CockpitDialog } from './CockpitDialog'
import { documentActionError, isVerificationPause, type MoveInput } from './useDocuments'

/**
 * "Andere Vorlage": re-parents a scan that was uploaded against the wrong
 * form.
 *
 * The move drops the old analysis — the result map was keyed by the old
 * template's boxes — so the dialog says that before it happens rather than
 * afterwards.
 *
 * The e-mail pause is handled where it lands: while an account is paused the
 * gateway refuses a move that asks for analysis **and moves nothing**, but
 * accepts `analyze: false`. That is the gateway's own documented retry, so
 * the refusal offers it as a button instead of a dead end.
 */
export function MoveDocumentDialog({
  open,
  onOpenChange,
  documentTitle,
  currentTemplateId,
  pending,
  error,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentTitle: string
  currentTemplateId: string
  pending: boolean
  error: unknown
  onSubmit: (input: Omit<MoveInput, 'documentId'>) => void
}) {
  const [selected, setSelected] = useState('')

  const templates = useQuery({
    queryKey: ['templates', 'move-targets'],
    queryFn: () => templatesApi.list({ pageSize: 100 }),
    enabled: open,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (open) setSelected('')
  }, [open, documentTitle])

  const targets = (templates.data ?? []).filter((template) => template.id !== currentTemplateId)
  const paused = error != null && isVerificationPause(error)

  return (
    <CockpitDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Zu anderer Vorlage verschieben"
      description={
        <>
          „{documentTitle}“ wird der gewählten Vorlage zugeordnet und dort neu analysiert. Das
          bisherige Ergebnis dieses Dokuments geht dabei verloren.
        </>
      }
      footer={
        <>
          <FxButton variant="outlined" onClick={() => onOpenChange(false)}>
            Abbrechen
          </FxButton>
          {paused ? (
            <FxButton
              disabled={!selected || pending}
              onClick={() => onSubmit({ targetTemplateId: selected, analyze: false })}
            >
              Nur verschieben, nicht analysieren
            </FxButton>
          ) : (
            <FxButton
              disabled={!selected || pending}
              onClick={() => onSubmit({ targetTemplateId: selected, analyze: true })}
            >
              Verschieben und analysieren
            </FxButton>
          )}
        </>
      }
    >
      {templates.isLoading ? (
        <FxSpinner label="Vorlagen werden geladen …" />
      ) : templates.error ? (
        <FxAlert tone="danger">Die Vorlagen konnten nicht geladen werden.</FxAlert>
      ) : targets.length === 0 ? (
        <FxAlert tone="info">Es gibt keine andere Vorlage, in die verschoben werden könnte.</FxAlert>
      ) : (
        <fieldset className="m-0 flex max-h-72 flex-col gap-1 overflow-y-auto border-0 p-0">
          <legend className="sr-only">Zielvorlage auswählen</legend>
          {targets.map((template) => (
            <label
              key={template.id}
              className="flex min-h-list-row cursor-pointer items-center gap-4 rounded-sm px-4 hover:bg-primary-subtle"
            >
              <input
                type="radio"
                name="zielvorlage"
                value={template.id}
                checked={selected === template.id}
                onChange={() => setSelected(template.id)}
                className="h-5 w-5 flex-none accent-[hsl(var(--fx-primary-strong))]"
              />
              <span className="min-w-0 flex-1 truncate">{template.title}</span>
            </label>
          ))}
        </fieldset>
      )}

      {error != null && (
        <FxAlert tone={paused ? 'warning' : 'danger'}>
          {documentActionError(error)}
          {paused && (
            <>
              {' '}
              Sie können das Dokument trotzdem verschieben und die Analyse später starten — nichts
              wurde bisher verschoben.
            </>
          )}
        </FxAlert>
      )}
    </CockpitDialog>
  )
}
